import { Component, OnInit } from '@angular/core';
import { SalesReportService } from 'src/app/services/reports/sales-report.service';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalService } from 'src/app/services/shared/global.service';

@Component({
  selector: 'app-sales-report',
  templateUrl: './sales-report.component.html',
  styleUrls: ['./sales-report.component.css']
})
export class SalesReportComponent implements OnInit{
  public errorMessage: string | null = null;
  sales: any[] = [];
  salesByModel: any[] = [];
  totalUnitsSold = 0;
  public isInProgress: boolean = false;
  form!: FormGroup;
  submitted: boolean = false;
  startDate!: string;
  endDate!: string;

  constructor(
    private salesReportService:SalesReportService,
    private globalService: GlobalService,
    private formBuilder: FormBuilder,
  ){
    this.form = this.formBuilder.group({
      date_from: ['', Validators.required],
      date_to: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    const today = new Date();
    const formatted = today.toISOString().split('T')[0];

    this.startDate = formatted;
    this.endDate = formatted;

    this.isInProgress=true;
    this.fetchReportData();
  }

  fetchReportData():void{
    this.salesReportService.getData()
    .subscribe({
      next: (response: any) => {
        if(response.status==='success'){
          this.sales = response.data.sales;
          this.salesByModel = response.data.sales_by_model;

          // count total units sold
          this.totalUnitsSold = this.sales.length;

          this.isInProgress=false;
        }else if(response.status==='no_data'){
           this.globalService.setGlobalWarningMessage(response.message);
           this.isInProgress=false;
        }else{
          this.errorMessage="Sorry, a problem was encountered while processing your request. Please refresh the page and try again!";
          this.globalService.setGlobalErrorMessage(this.errorMessage);
          this.isInProgress=false;
        }
      },
      error: (error) => {
        console.log(error)
        this.globalService.setGlobalErrorMessage(error);
        this.isInProgress=false;
      }
    });
  }

  filterByDate(){
    this.submitted=true;
    this.isInProgress = true;

    if (this.form.valid) {
      this.startDate = this.form.value.date_from;
      this.endDate = this.form.value.date_to;

     this.salesReportService.filterByDate(this.form.value.date_from,this.form.value.date_to)
      .subscribe({
        next: (response: any) => {
          if(response.status==='success'){
            this.isInProgress=false;
            this.sales = response.data.sales;
            this.salesByModel = response.data.sales_by_model;

            // count total units sold
            this.totalUnitsSold = this.sales.length;
          }else if(response.status==='no_data'){
            this.isInProgress=false;
            this.sales=[];
            this.salesByModel=[];
            this.globalService.setGlobalWarningMessage(response.message);
          }else{
            this.isInProgress=false;
            const errorMessage="Sorry, a problem was encountered while filtering sales report. Please try again!";
            this.globalService.setGlobalErrorMessage(errorMessage);
          }
        },
        error: (error) => {
          this.isInProgress=false;
          this.globalService.setGlobalErrorMessage(error);
        }
      });
    }else{
      this.isInProgress = false;
    }
  }

  exportToPdf() {
    const pdf = new jsPDF('p', 'mm', 'a4');

    // Title

    const title = `Sales Report for the period from ${this.formatPdfDate(
      this.startDate
    )} to ${this.formatPdfDate(this.endDate)}`;

    pdf.setFontSize(14);
    pdf.text(title, 105, 15, { align: 'center' });

    /* =========================
       FIRST TABLE: SALES
       ========================= */
    autoTable(pdf, {
      html: '#salesTable',
      theme: 'striped',
      startY: 25,
      headStyles: { fillColor: [40, 167, 69], textColor: 255 },
      styles: { fontSize: 9, cellPadding: 3 },
      didDrawPage: (data) => this.drawFooter(pdf)
    });

    // Capture where the first table ended
    const finalY = (pdf as any).lastAutoTable.finalY + 10;

    /* =========================
       SECOND TABLE: SUMMARY
       ========================= */
    pdf.setFontSize(12);
    pdf.text('Summary', 10, finalY - 3);

    autoTable(pdf, {
      html: '#summaryTable',
      theme: 'striped',
      startY: finalY + 2,
      headStyles: { fillColor: [40, 167, 69], textColor: 255 },
      styles: { fontSize: 9, cellPadding: 3 },
      didDrawPage: (data) => this.drawFooter(pdf)
    });

    pdf.save('sales-report.pdf');
  }

  drawFooter(pdf: jsPDF) {
    const pageCount = pdf.getNumberOfPages();
    const pageSize = pdf.internal.pageSize;
    const pageHeight = pageSize.height || pageSize.getHeight();

    // Page number
    pdf.setFontSize(10);
    pdf.text(
      `Page ${pdf.getCurrentPageInfo().pageNumber} of ${pageCount}`,
      pageSize.width / 2,
      pageHeight - 10,
      { align: 'center' }
    );

    // Generated date
    const now = new Date();
    const formatted = now.toLocaleString('en-GB');

    pdf.text(`Generated On: ${formatted}`, 10, pageHeight - 10);
  }

  formatPdfDate(dateStr: string): string {
    const d = new Date(dateStr);

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  }
}
